import * as Sentry from "@sentry/react";
import { create } from "zustand";
import Auth from "../Auth";
import { sendAppEvent } from "../Utils";

export type StepAnswer = string | string[] | boolean;
export type AnswersMap = Partial<Record<StepIds, StepAnswer>>;

export enum StepType {
  SingleChoice = "single-choice",
  MultiChoiceCloud = "multi-choice-cloud",
  MultiChoiceVStack = "multi-choice-vstack",
  Custom = "custom",
  Loading = "loading",
}

export enum StepIds {
  WritingJourney = "writing-journey",
  WritingStyle = "writing-style",
  CurrentWork = "current-work",
  SeriesQuestion = "series-question",
  SeriesStage = "series-stage",
  ImportStep = "import-step",
  Genre = "genre",
  HelpType = "help-type",
  OnboardingDecision = "onboarding-decision",
  Braindump = "braindump",
  BraindumpNotSure = "braindump-not-sure",
  HelpTypeSomethingElse = "help-type-something-else",
  DescribePlugin = "describe-plugin",
  PluginResult = "plugin-result",
}

export const Done = "done";
export type Done = typeof Done;

type DynamicTitle<TAnswers> = string | ((answers: TAnswers) => string);

type BaseStep<TStepsEnum extends string, TAnswers> = {
  id: string;
  title: DynamicTitle<TAnswers>;
  type: StepType;
  subtitle?: DynamicTitle<TAnswers>;
  getNextStep: (answers: TAnswers) => TStepsEnum | Done;
};

type SingleChoiceOption = {
  value: string;
  label: string;
  description?: string;
};

type SingleChoiceStep<TStepsEnum extends string, TAnswers> = BaseStep<TStepsEnum, TAnswers> & {
  type: StepType.SingleChoice;
  options: SingleChoiceOption[];
};

type MultiChoiceCloudStep<TStepsEnum extends string, TAnswers> = BaseStep<TStepsEnum, TAnswers> & {
  type: StepType.MultiChoiceCloud;
  options: string[];
};

type MultiChoiceVStackStep<TStepsEnum extends string, TAnswers> = BaseStep<TStepsEnum, TAnswers> & {
  type: StepType.MultiChoiceVStack;
  options: SingleChoiceOption[];
};

type CustomStep<TStepsEnum extends string, TAnswers> = BaseStep<TStepsEnum, TAnswers> & {
  type: StepType.Custom;
};

type LoadingStep<TStepsEnum extends string, TAnswers> = BaseStep<TStepsEnum, TAnswers> & {
  type: StepType.Loading;
};

export type Step<TStepsEnum extends string, TAnswers> =
  | SingleChoiceStep<TStepsEnum, TAnswers>
  | MultiChoiceCloudStep<TStepsEnum, TAnswers>
  | MultiChoiceVStackStep<TStepsEnum, TAnswers>
  | CustomStep<TStepsEnum, TAnswers>
  | LoadingStep<TStepsEnum, TAnswers>;

type OnboardingFlow<TStepsEnum extends string, TAnswers> = {
  first: TStepsEnum;
  steps: Record<TStepsEnum, Step<TStepsEnum, TAnswers>>;
};

const onboardingSteps: OnboardingFlow<StepIds, AnswersMap> = {
  first: StepIds.WritingJourney,
  steps: {
    [StepIds.WritingJourney]: {
      id: StepIds.WritingJourney,
      title: "How would you describe yourself as a writer?",
      type: StepType.SingleChoice,
      getNextStep: (_answers: AnswersMap): StepIds | Done => {
        return StepIds.WritingStyle;
      },
      options: [
        {
          value: "professional",
          label: "Professional",
        },
        {
          value: "aspiring-professional",
          label: "Aspiring Professional",
        },
        {
          value: "hobbyist",
          label: "Hobbyist",
        },
        {
          value: "student",
          label: "Student",
        },
        {
          value: "other",
          label: "Other",
        },
      ],
    },
    [StepIds.WritingStyle]: {
      id: StepIds.WritingStyle,
      title: "Wonderful, and what’s your writing process like?",
      type: StepType.SingleChoice,
      getNextStep: (_answers: AnswersMap): StepIds | Done => {
        return StepIds.CurrentWork;
      },
      options: [
        {
          value: "pantser",
          label: "I fly by the seat of my pants",
          description: "Call me a pantser",
        },
        {
          value: "plotter",
          label: "I like to work from an outline",
          description: "Call me a planner",
        },
        {
          value: "panster-and-plotter",
          label: "A little bit of both",
          description: "I defy labels",
        },
      ],
    },
    [StepIds.CurrentWork]: {
      id: StepIds.CurrentWork,
      title: "Good to know! I’d love to help you with one of your projects. What would you like help with?",
      type: StepType.SingleChoice,
      getNextStep: (_answers: AnswersMap): StepIds | Done => {
        return StepIds.SeriesQuestion;
      },
      options: [
        {
          value: "work-in-progress",
          label: "A work-in-progress",
          description: "Unfinished manuscript? Halfway through edits? Start here.",
        },
        {
          value: "new-book",
          label: "A new book",
          description: "If you’re not sure what to pick, start here!",
        },
      ],
    },
    [StepIds.SeriesQuestion]: {
      id: StepIds.SeriesQuestion,
      title: (answers: AnswersMap) => {
        const currentWork = answers[StepIds.CurrentWork];
        const prefix = currentWork === "new-book" ? "Exciting, nothing like that new book energy.\n" : "";
        return `${prefix}Is this book going to be a part of a series?`;
      },
      type: StepType.SingleChoice,
      getNextStep: (answers: AnswersMap): StepIds | Done => {
        const isSeries = answers[StepIds.SeriesQuestion] === "series";
        if (isSeries) {
          return StepIds.SeriesStage;
        }
        if (answers[StepIds.CurrentWork] === "work-in-progress") {
          return StepIds.ImportStep;
        }

        return StepIds.Genre; // otherwise its a standalone new book
      },
      options: [
        {
          value: "series",
          label: "Part of a series",
          description: "Episodic or sequential",
        },
        {
          value: "standalone",
          label: "Not part of a series",
          description: "One and done",
        },
      ],
    },
    [StepIds.SeriesStage]: {
      id: StepIds.SeriesStage,
      title: "Great, series are quite popular. Are you kicking off a new series, or adding to one you’ve already started?",
      type: StepType.SingleChoice,
      getNextStep: (answers: AnswersMap): StepIds | Done => {
        const isNewBook = answers[StepIds.CurrentWork] === "new-book";
        const isKickingOff = answers[StepIds.SeriesStage] === "kicking-off";
        if (isNewBook && isKickingOff) {
          return StepIds.Genre;
        }
        return StepIds.ImportStep;
      },
      options: [
        {
          value: "kicking-off",
          label: "Kicking off",
          description: "Book one in a new world",
        },
        {
          value: "adding-to-existing-series",
          label: "Adding to it",
          description: "Picking up where things left off",
        },
      ],
    },
    [StepIds.ImportStep]: {
      id: StepIds.ImportStep,
      title: (answers: AnswersMap) => {
        const currentWork = answers[StepIds.CurrentWork];
        const saidSeries = answers[StepIds.SeriesQuestion] === "series";
        const seriesStage = answers[StepIds.SeriesStage];
        const existingSeries = saidSeries && seriesStage === "adding-to-existing-series";

        if (currentWork === "new-book" && existingSeries) {
          return "Would you like to import your previous book?";
        }
        if (currentWork === "work-in-progress" && existingSeries) {
          // keeping this a separate if-block in case we want to add functionality
          // to import both the current work and the previous book in the series
          return "Let's import your work";
        }

        return "Let's import your work";
      },
      type: StepType.Custom,
      subtitle: (answers: AnswersMap) => {
        const currentWork = answers[StepIds.CurrentWork];
        const saidSeries = answers[StepIds.SeriesQuestion] === "series";
        const seriesStage = answers[StepIds.SeriesStage];
        const existingSeries = saidSeries && seriesStage === "adding-to-existing-series";

        if (currentWork === "new-book" && existingSeries) {
          return "We'll create a Story Bible for you to help you keep your characters, worldbuilding, and plot lines straight.";
        }
        if (currentWork === "work-in-progress" && existingSeries) {
          // keeping this a separate if-block in case we want to add functionality
          // to import both the current work and the previous book in the series
          return "Sudowrite will fill out a Story Bible for you";
        }
        return "Sudowrite will fill out a Story Bible for you";
      },
      getNextStep: (): StepIds | Done => {
        return StepIds.Genre;
      },
    },
    [StepIds.Genre]: {
      id: StepIds.Genre,
      title: "What genre(s) is your book?",
      type: StepType.MultiChoiceCloud,
      getNextStep: (): StepIds | Done => {
        return StepIds.HelpType;
      },
      options: [
        "Fantasy",
        "Romance",
        "Erotica",
        "Sci-Fi",
        "Young Adult",
        "Children's",
        "Non-Fiction",
        "Thriller",
        "Mystery",
        "Literary Fiction",
        "Historical",
        "Paranormal",
        "Horror",
        "Contemporary Fiction",
        "Adventure",
        "Dystopian",
        "Other",
      ],
    },
    [StepIds.HelpType]: {
      id: StepIds.HelpType,
      title: "What kind of support do you want from Sudowrite?",
      type: StepType.MultiChoiceVStack,
      getNextStep: (answers: AnswersMap): StepIds | Done => {
        const helpTypes = answers[StepIds.HelpType] as string[] | undefined;
        const hasEditing = helpTypes?.includes("editing");

        if (hasEditing) {
          return Done;
        }

        const shouldShowBraindump = answers[StepIds.CurrentWork] === "new-book" || answers[StepIds.ImportStep] === "skipped";
        if (shouldShowBraindump) {
          return StepIds.Braindump;
        }
        return Done;
      },
      options: [
        {
          value: "writing",
          label: "Help me write",
          description: "Help me hit my word count goal",
        },
        {
          value: "editing",
          label: "Help me edit",
          description: "I'll write the prose, you help me improve it",
        },
        {
          value: "something-else",
          label: "Something else",
          description: "Something outside writing or editing",
        },
      ],
    },
    [StepIds.OnboardingDecision]: {
      id: StepIds.OnboardingDecision,
      title: "Setting up your personalized experience...",
      type: StepType.Loading,
      subtitle: "We're analyzing your responses to create the perfect onboarding flow",
      getNextStep: (): StepIds | Done => {
        return Done;
      },
      // This step will determine the final onboarding path:
      // - New book + plotter/hybrid → Story Bible onboarding
      // - New book + pantser → Write and rewrite onboarding
      // - WIP → Write and rewrite onboarding
    },
    [StepIds.DescribePlugin]: {
      id: StepIds.DescribePlugin,
      title: "Get personalized feedback with Plugins.",
      subtitle: "We'll create a custom tool you can use in Sudowrite. Just tell us what kind of feedback you'd like.",
      type: StepType.Custom,
      getNextStep: (answers: AnswersMap): StepIds | Done => {
        if (answers[StepIds.DescribePlugin] === "skipped") {
          return Done;
        }
        return StepIds.PluginResult;
      },
    },
    [StepIds.PluginResult]: {
      id: StepIds.PluginResult,
      title: "We made your custom plugin!",
      type: StepType.Custom,
      getNextStep: (_answers: AnswersMap): StepIds | Done => {
        return Done;
      },
    },
    [StepIds.Braindump]: {
      id: StepIds.Braindump,
      title: "Let's set up your first project!",
      subtitle: "What is your story about?",
      type: StepType.Custom,
      getNextStep: (): StepIds | Done => {
        return Done;
      },
    },
    [StepIds.BraindumpNotSure]: {
      id: StepIds.BraindumpNotSure,
      title: "This is not a real step, just an answer.",
      subtitle: "null",
      type: StepType.Custom,
      getNextStep(answers) {
        return Done;
      },
    },
    [StepIds.HelpTypeSomethingElse]: {
      id: StepIds.HelpTypeSomethingElse,
      title: "This is also not a real step, just an answer.",
      type: StepType.Custom,
      getNextStep: (): StepIds | Done => {
        return Done;
      },
    },
  },
};

export type GeneratedPlugin = {
  name: string;
  description: string;
  shortId: string;
  slug: string;
};

export type OnboardingStore = {
  isActive: boolean;
  currentStepId: StepIds | Done | null;
  flow: OnboardingFlow<StepIds, AnswersMap>;
  answers: AnswersMap;
  stack: string[];
  stepIdToIndex: Record<string, number>;
  generatedPlugin: GeneratedPlugin | null;
  startOnboarding: (stepId?: StepIds) => void;
  recordAnswer: (stepId: StepIds, answerValue: StepAnswer) => void;
  setGeneratedPlugin: (plugin: GeneratedPlugin) => void;
  markAsCompleted: (options?: { skipDraft?: boolean }) => Promise<void>;
  next: () => Promise<void>;
  back: () => void;
  skipOnboarding: () => void;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  isActive: false,
  currentStepId: null,
  flow: onboardingSteps,
  answers: {
    [StepIds.Genre]: [],
    [StepIds.HelpType]: [],
  },
  stack: [],
  stepIdToIndex: {},
  generatedPlugin: null,

  startOnboarding: (stepId?: StepIds) => {
    set({
      isActive: true,
      currentStepId: stepId ?? onboardingSteps.first,
    });
    const stepIds = Object.keys(onboardingSteps.steps);
    const stepIdToIndex: Record<string, number> = {};
    stepIds
      .filter((stepId) => stepId !== "first")
      .forEach((stepId, index) => {
        stepIdToIndex[stepId] = index;
      });
    set({ stepIdToIndex });
    console.log("stepIdToIndex", get().stepIdToIndex);
  },

  recordAnswer: (stepId: string, answerValue: StepAnswer) => {
    set((state) => ({
      answers: {
        ...state.answers,
        [stepId]: answerValue,
      },
    }));
  },

  setGeneratedPlugin: (plugin: GeneratedPlugin) => {
    set({ generatedPlugin: plugin });
  },

  markAsCompleted: async (options?: { skipDraft?: boolean }) => {
    await Auth.fetchWithIdToken("/api/user-metadata/survey", {
      survey: get().answers,
      isCompleted: true,
      version: "IMPROVE_ONBOARDING_V1",
    });

    // Chat onboarding skips the tour draft: the first
    // experience is the chat opener, not pre-generated prose in the editor.
    if (options?.skipDraft) return;

    Auth.fetchWithIdToken("/api/ai/generateOnboardingDraft", {}).catch((err) => {
      Sentry.captureException(err);
    });
  },

  next: async () => {
    const currentStepId = get().currentStepId;
    if (!currentStepId) return;
    const currentStep = onboardingSteps.steps[currentStepId as keyof typeof onboardingSteps.steps];
    const nextStepId = currentStep.getNextStep(get().answers);

    sendAppEvent("survey_step_submitted", {
      step_name: currentStepId,
      survey_variation: "IMPROVE_ONBOARDING_V1",
      step_value: get().answers[currentStepId as StepIds],
    });

    // Custom plugin onboarding skips Braindump.
    // Record BraindumpNotSure so downstream draft behavior stays consistent.
    if (currentStepId === StepIds.PluginResult) {
      set((state) => ({
        answers: {
          ...state.answers,
          [StepIds.BraindumpNotSure]: true,
        },
      }));
    }

    // Skip the intermediate save when transitioning to Done — markAsCompleted
    // handles the final persist and would race with this fire-and-forget call.
    if (nextStepId !== Done) {
      Auth.fetchWithIdToken("/api/user-metadata/survey", {
        survey: get().answers,
        version: "IMPROVE_ONBOARDING_V1",
      });
    }

    set((state) => ({
      stack: [...state.stack, currentStepId],
    }));
    set({ currentStepId: nextStepId });
  },

  back: () => {
    const stack = get().stack;
    const previousStepId = stack[stack.length - 1];
    const newStack = stack.slice(0, -1);
    set({ stack: newStack, currentStepId: previousStepId as StepIds | null });
  },

  skipOnboarding: () => set({ isActive: false }),
}));
